<template lang="pug">
v-card.dashboard-metric-card(
  :class="{ 'dashboard-metric-card--compact': compact, 'dashboard-metric-card--split': hasSecondary }",
  :style="cardStyle"
)
  v-card-text.dashboard-metric-card__content(
    :class="{ 'dashboard-metric-card__content--with-info': infoText }"
  )
    .dashboard-metric-card__icon(
      v-if="icon && !compact",
      :style="iconStyle"
    )
      v-icon(size="20", color="white") {{ icon }}

    template(v-if="hasSecondary")
      .dashboard-metric-card__split
        .dashboard-metric-card__split-pane
          .dashboard-metric-card__label {{ title }}
          .dashboard-metric-card__value {{ formattedValue }}
        .dashboard-metric-card__split-pane
          .dashboard-metric-card__label {{ secondaryTitle }}
          .dashboard-metric-card__value {{ formattedSecondaryValue }}
    template(v-else)
      .dashboard-metric-card__label {{ title }}
      v-tooltip(v-if="infoText", top)
        template(v-slot:activator="{ on, attrs }")
          button.dashboard-metric-card__info-button(
            type="button",
            v-bind="attrs",
            v-on="on"
            :aria-label="`Informações sobre ${title}`"
          )
            v-icon(size="14", color="#7d94b8") mdi-information-outline
        span {{ infoText }}
      .dashboard-metric-card__value {{ formattedValue }}
      .dashboard-metric-card__subtitle(v-if="subtitle && !compact") {{ subtitle }}
</template>

<script>
export default {
  name: "MetricCard",
  props: {
    title: {
      type: String,
      required: true
    },
    value: {
      type: [String, Number],
      required: true
    },
    subtitle: {
      type: String,
      default: ""
    },
    secondaryTitle: {
      type: String,
      default: ""
    },
    secondaryValue: {
      type: [String, Number],
      default: ""
    },
    infoText: {
      type: String,
      default: ""
    },
    icon: {
      type: String,
      default: ""
    },
    accentColor: {
      type: String,
      default: "#3d82f6"
    },
    iconBackgroundColor: {
      type: String,
      default: ""
    },
    compact: {
      type: Boolean,
      default: false
    }
  },
  computed: {
    cardStyle() {
      if (this.compact) {
        return {
          "--metric-border-color": this.accentColor
        };
      }

      return {
        "--metric-top-border-color": this.accentColor
      };
    },
    iconStyle() {
      return {
        backgroundColor: this.iconBackgroundColor || this.accentColor
      };
    },
    formattedValue() {
      return this.formatValue(this.value);
    },
    formattedSecondaryValue() {
      return this.formatValue(this.secondaryValue);
    },
    hasSecondary() {
      return Boolean(this.secondaryTitle);
    }
  },
  methods: {
    formatValue(value) {
      if (typeof value === "number") {
        return new Intl.NumberFormat("pt-BR").format(value);
      }

      return String(value);
    }
  }
};
</script>

<style scoped>
.dashboard-metric-card {
  width: 100%;
  min-height: 178px;
  height: 178px;
  max-height: 178px;
  border: 1px solid #d9e2ef !important;
  border-top: 3px solid var(--metric-top-border-color, #d9e2ef) !important;
  border-radius: 10px !important;
  box-shadow: 0 2px 6px rgba(15, 42, 77, 0.08) !important;
  overflow: hidden;
}

.dashboard-metric-card--compact {
  min-height: 98px;
  height: 98px;
  max-height: 98px;
  border: 1px solid var(--metric-border-color, #d9e2ef) !important;
  border-top: 1px solid var(--metric-border-color, #d9e2ef) !important;
}

.dashboard-metric-card__content {
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  height: 100%;
  padding: 12px 14px !important;
}

.dashboard-metric-card__icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  margin-bottom: 10px;
  border-radius: 10px;
}

.dashboard-metric-card__label {
  width: 100%;
  min-width: 0;
  color: #5f6b7a;
  font-size: 12px;
  font-weight: 700;
  line-height: 1.3;
}

.dashboard-metric-card__content--with-info .dashboard-metric-card__label {
  max-width: calc(100% - 24px);
}

.dashboard-metric-card__info-button {
  position: absolute;
  top: 12px;
  right: 14px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 18px;
  height: 18px;
  padding: 0;
  border: 0;
  border-radius: 999px;
  background: transparent;
  cursor: help;
}

.dashboard-metric-card__value {
  margin-top: 6px;
  color: #173256;
  font-size: 28px;
  font-weight: 700;
  line-height: 1;
}

.dashboard-metric-card__subtitle {
  margin-top: auto;
  padding-top: 10px;
  color: #5f6b7a;
  font-size: 12px;
  font-weight: 600;
  line-height: 1.2;
}

.dashboard-metric-card--compact .dashboard-metric-card__content {
  justify-content: flex-start;
  padding: 14px 16px !important;
}

.dashboard-metric-card--compact .dashboard-metric-card__label {
  color: #31496b;
  font-size: 14px;
  font-weight: 600;
  margin-bottom: 0;
}

.dashboard-metric-card--compact .dashboard-metric-card__info-button {
  top: 14px;
  right: 16px;
}

.dashboard-metric-card--compact .dashboard-metric-card__value {
  margin-top: 0;
  color: #0a1f44;
  font-size: 20px;
  font-weight: 700;
  line-height: 1;
}

.dashboard-metric-card__split {
  display: flex;
  align-items: stretch;
  width: 100%;
  height: 100%;
}

.dashboard-metric-card__split-pane {
  flex: 1 1 0;
  min-width: 0;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
}

.dashboard-metric-card__split-pane + .dashboard-metric-card__split-pane {
  padding-left: 12px;
  margin-left: 12px;
  border-left: 1px solid #e5ecf4;
  text-align: right;
}

.dashboard-metric-card__split-pane
  + .dashboard-metric-card__split-pane
  .dashboard-metric-card__label,
.dashboard-metric-card__split-pane
  + .dashboard-metric-card__split-pane
  .dashboard-metric-card__value {
  text-align: right;
}

@media (max-width: 959px) {
  .dashboard-metric-card {
    min-height: 160px;
    height: 160px;
    max-height: 160px;
  }

  .dashboard-metric-card--compact {
    min-height: 92px;
    height: 92px;
    max-height: 92px;
  }
}
</style>
